// Browser-only, no external libraries.
// Packs a FileList from <input webkitdirectory> into a .tar.gz (or .tar as fallback).

const encoder = new TextEncoder();
const byteLen = (s) => encoder.encode(s).length;

// true  -> "mymodel/saved_model.pb" is stored as "saved_model.pb"
// false -> keeps "mymodel/saved_model.pb"
export const STRIP_ROOT_FOLDER = true;

const MAX_TAR_FILE_SIZE = 0o77777777777; // 8 GB limit of the classic tar size field

const writeStr = (buf, str, offset, length) => {
	buf.set(encoder.encode(str).subarray(0, length), offset);
};

const writeOctal = (buf, num, offset, length) => {
	writeStr(buf, num.toString(8).padStart(length - 1, '0') + '\0', offset, length);
};

const splitPath = (fullPath) => {
	if (byteLen(fullPath) <= 100) return { prefix: '', name: fullPath };

	// ustar: name <= 100 bytes, prefix <= 155 bytes, split on a '/'
	for (let i = 0; i < fullPath.length; i++) {
		if (fullPath[i] !== '/') continue;
		const prefix = fullPath.slice(0, i);
		const name = fullPath.slice(i + 1);
		if (byteLen(prefix) <= 155 && byteLen(name) <= 100) return { prefix, name };
	}
	throw new Error(`Path too long: ${fullPath}`);
};

const makeTarHeader = (fullPath, size, mtime) => {
	if (size > MAX_TAR_FILE_SIZE) throw new Error(`File too large: ${fullPath}`);

	const { prefix, name } = splitPath(fullPath);
	const header = new Uint8Array(512);

	writeStr(header, name, 0, 100);
	writeOctal(header, 0o644, 100, 8);   // mode
	writeOctal(header, 0, 108, 8);       // uid
	writeOctal(header, 0, 116, 8);       // gid
	writeOctal(header, size, 124, 12);   // size
	writeOctal(header, mtime, 136, 12);  // mtime
	header.fill(0x20, 148, 156);         // checksum placeholder (spaces)
	header[156] = 0x30;                  // typeflag '0' = regular file
	writeStr(header, 'ustar\0', 257, 6);
	writeStr(header, '00', 263, 2);
	writeStr(header, prefix, 345, 155);

	let sum = 0;
	for (const b of header) sum += b;
	writeStr(header, sum.toString(8).padStart(6, '0') + '\0 ', 148, 8);

	return header;
};

// Skip hidden files / folders such as .DS_Store, .git, .ipynb_checkpoints
const isHidden = (relPath) => relPath.split('/').some((seg) => seg.startsWith('.'));

export const folderToArchive = async (fileList) => {
	const all = Array.from(fileList || []);
	if (!all.length) throw new Error('The selected folder is empty');

	const rootName = (all[0].webkitRelativePath || '').split('/')[0] || 'model';

	const entries = all
		.map((file) => {
			const rel = file.webkitRelativePath || file.name;
			const stored = STRIP_ROOT_FOLDER ? rel.split('/').slice(1).join('/') : rel;
			return { file, rel, stored };
		})
		.filter((e) => e.stored && !isHidden(e.rel));

	if (!entries.length) throw new Error('The selected folder has no usable files');

	const parts = [];
	for (const { file, stored } of entries) {
		parts.push(makeTarHeader(stored, file.size, Math.floor(file.lastModified / 1000)));
		parts.push(file); // Blob parts are referenced, not copied into JS memory
		const pad = (512 - (file.size % 512)) % 512;
		if (pad) parts.push(new Uint8Array(pad));
	}
	parts.push(new Uint8Array(1024)); // end-of-archive marker

	const tarBlob = new Blob(parts);

	// Older browsers: send the plain .tar (also allowed by the backend)
	if (typeof CompressionStream === 'undefined') {
		return new File([tarBlob], `${rootName}.tar`, { type: 'application/x-tar' });
	}

	const gzStream = tarBlob.stream().pipeThrough(new CompressionStream('gzip'));
	const gzBlob = await new Response(gzStream).blob();
	return new File([gzBlob], `${rootName}.tar.gz`, { type: 'application/gzip' });
};